import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaChevronUp,
  FaChevronDown,
  FaBuilding,
  FaRegClock,
  FaShieldAlt,
  FaCode,
  FaLock,
  FaGlobe,
  FaServer,
  FaPalette,
  FaStar,
  FaCheckCircle,
} from "react-icons/fa";
import { experienceData } from "../data/portfolioData";

// Professional gradient backgrounds for icons
const getIconGradient = (index) => {
  const gradients = [
    "from-emerald-500 to-teal-500",
    "from-blue-500 to-cyan-500",
    "from-purple-500 to-indigo-500",
    "from-rose-500 to-pink-500",
    "from-amber-500 to-orange-500",
    "from-emerald-600 to-green-500",
  ];
  return gradients[index % gradients.length];
};

// Professional icons based on index
const getIcon = (index) => {
  const icons = [
    <FaShieldAlt className="text-lg" />,
    <FaCode className="text-lg" />,
    <FaLock className="text-lg" />,
    <FaGlobe className="text-lg" />,
    <FaServer className="text-lg" />,
    <FaPalette className="text-lg" />,
  ];
  return icons[index % icons.length];
};

const Experience = () => {
  const [offset, setOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [expandedTech, setExpandedTech] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Desktop constants
  const VISIBLE = 3;
  const CARD_HEIGHT = 280;
  const GAP = 24;
  const STEP = CARD_HEIGHT + GAP;

  // Use data from portfolioData.js
  const allExperiences = useMemo(() => {
    return experienceData.map((exp, index) => ({
      id: index,
      title: exp.title,
      company: exp.company,
      period: exp.period,
      location: exp.location || "Remote",
      description: exp.description,
      technologies: exp.technologies || [],
      achievements: exp.achievements || [],
      isCurrent:
        exp.period.toLowerCase().includes("present") ||
        exp.period.toLowerCase().includes("current"),
      icon: getIcon(index),
      iconGradient: getIconGradient(index),
    }));
  }, []);

  // Desktop calculations
  const maxOffset = Math.max(0, (allExperiences.length - VISIBLE) * STEP);
  const canUp = offset > 0;
  const canDown = offset < maxOffset;
  const activeStart = Math.round(offset / STEP);

  const toggleTechExpand = (cardId) => {
    setExpandedTech((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  const scroll = useCallback(
    (dir) => {
      if (isAnimating) return;
      if (dir === "up" && !canUp) return;
      if (dir === "down" && !canDown) return;
      setIsAnimating(true);
      setOffset((prev) => {
        const next = dir === "down" ? prev + STEP : prev - STEP;
        return Math.max(0, Math.min(next, maxOffset));
      });
      setTimeout(() => setIsAnimating(false), 520);
    },
    [isAnimating, canUp, canDown, maxOffset],
  );

  // ==================== DESKTOP EXPERIENCE CARD ====================
  const DesktopExperienceCard = ({ experience, index }) => {
    const isExpanded = expandedTech[experience.id] || false;
    const displayTechnologies = isExpanded
      ? experience.technologies
      : experience.technologies.slice(0, 4);
    const hasMoreTechs = experience.technologies.length > 4 && !isExpanded;

    return (
      <section
        id="experience"
        className="relative w-full transition-all duration-300 "
        style={{ height: CARD_HEIGHT }}
      >
        <div className="relative h-full bg-white dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden group/card">
          {/* Animated gradient line */}
          <div className="absolute top-0 left-0 right-0 h-1 overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${experience.iconGradient} transform -translate-x-full group-hover/card:translate-x-0 transition-transform duration-500 ease-out`}
              style={{ width: "100%" }}
            />
          </div>

          {/* Professional corner accent */}
          <div
            className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${experience.iconGradient} opacity-0 group-hover/card:opacity-10 transition-opacity duration-500 rounded-bl-2xl`}
          />

          {/* Current/Completed Badge */}
          <div className="absolute top-4 right-4 z-10">
            {experience.isCurrent ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-semibold bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-lg shadow-md">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                </span>
                Current
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium bg-gray-100 dark:bg-gray-700/80 text-gray-600 dark:text-gray-300 rounded-lg backdrop-blur-sm">
                <FaRegClock className="text-sm" />
                Completed
              </span>
            )}
          </div>

          <div className="p-6 flex flex-col h-full">
            <div className="flex items-start gap-4 mb-4">
              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-r ${experience.iconGradient} flex items-center justify-center text-white shadow-lg flex-shrink-0`}
              >
                {experience.icon}
              </div>
              <div className="flex-1 min-w-0 pr-16">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight line-clamp-1 mb-1">
                  {experience.title}
                </h3>
                <div className="flex items-center gap-1.5">
                  <FaBuilding className="text-emerald-500 dark:text-emerald-400 text-sm" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                    {experience.company}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
                <FaCalendarAlt className="text-emerald-500 text-sm" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {experience.period}
                </span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20">
                <FaMapMarkerAlt className="text-cyan-500 text-sm" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {experience.location}
                </span>
              </div>
              <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20">
                <FaStar className="text-purple-500 text-sm" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Top Performer
                </span>
              </div>
            </div>

            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-3 line-clamp-2">
              {experience.description}
            </p>

            {experience.achievements && experience.achievements.length > 0 && (
              <div className="mb-2 flex items-center gap-1.5">
                <FaCheckCircle className="text-emerald-500 text-sm" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {experience.achievements.length} key achievements
                </span>
              </div>
            )}

            {experience.technologies && experience.technologies.length > 0 && (
              <div className="mt-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                    Technologies
                  </span>
                  {hasMoreTechs && (
                    <button
                      onClick={() => toggleTechExpand(experience.id)}
                      className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 font-medium"
                    >
                      +{experience.technologies.length - 4} more
                    </button>
                  )}
                  {isExpanded && (
                    <button
                      onClick={() => toggleTechExpand(experience.id)}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-600 font-medium"
                    >
                      Show less
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {displayTechnologies.map((tech, idx) => (
                    <span
                      key={idx}
                      className="text-sm px-3 py-1 rounded-lg bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700/50 dark:to-gray-700/30 text-gray-700 dark:text-gray-300 font-medium border border-gray-200 dark:border-gray-600"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  // ==================== MOBILE EXPERIENCE CARD (Simple & Clean) ====================
  const MobileExperienceCard = ({ experience }) => {
    const isExpanded = expandedTech[experience.id] || false;
    const displayTechnologies = isExpanded
      ? experience.technologies
      : experience.technologies.slice(0, 4);
    const hasMoreTechs = experience.technologies.length > 4 && !isExpanded;

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
        <div className="p-4">
          {/* Header */}
          <div className="flex items-start gap-3 mb-3">
            <div
              className={`w-10 h-10 rounded-xl bg-gradient-to-r ${experience.iconGradient} flex items-center justify-center text-white shadow-md flex-shrink-0`}
            >
              {experience.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                  {experience.title}
                </h3>
                {experience.isCurrent && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-md flex-shrink-0">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                    </span>
                    Current
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 mt-1">
                <FaBuilding className="text-emerald-500 text-[10px]" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {experience.company}
                </span>
              </div>
            </div>
          </div>

          {/* Period and Location */}
          <div className="flex flex-wrap gap-2 mb-3">
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700/50">
              <FaCalendarAlt className="text-emerald-500 text-[10px]" />
              <span className="text-[10px] text-gray-600 dark:text-gray-400">
                {experience.period}
              </span>
            </div>
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700/50">
              <FaMapMarkerAlt className="text-cyan-500 text-[10px]" />
              <span className="text-[10px] text-gray-600 dark:text-gray-400">
                {experience.location}
              </span>
            </div>
          </div>

          {/* Description */}
          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-3">
            {experience.description}
          </p>

          {/* Technologies */}
          {experience.technologies && experience.technologies.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                  Tech Stack
                </span>
                {hasMoreTechs && (
                  <button
                    onClick={() => toggleTechExpand(experience.id)}
                    className="text-[10px] text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 font-medium"
                  >
                    +{experience.technologies.length - 4} more
                  </button>
                )}
                {isExpanded && (
                  <button
                    onClick={() => toggleTechExpand(experience.id)}
                    className="text-[10px] text-gray-500 dark:text-gray-400 hover:text-gray-600 font-medium"
                  >
                    Show less
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {displayTechnologies.map((tech, idx) => (
                  <span
                    key={idx}
                    className="text-[9px] px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section
      id="experience"
      className="relative min-h-screen flex items-center justify-center py-8 sm:py-12 overflow-hidden"
    >
      {/* Desktop Background */}
      {!isMobile && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900" />
          <div className="absolute inset-0 dark:opacity-5 opacity-10 pointer-events-none">
            <div className="absolute inset-0 bg-grid-pattern" />
          </div>
          <div className="absolute top-20 -left-20 w-72 h-72 bg-emerald-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob" />
          <div className="absolute bottom-20 -right-20 w-72 h-72 bg-cyan-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000" />
        </>
      )}

      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">
            Professional <span className="gradient-text">Experience</span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed max-w-2xl mx-auto mt-2">
            A journey through my professional career in security and development
          </p>
        </div>

        {/* ==================== DESKTOP VIEW (Timeline Carousel) ==================== */}
        {!isMobile && (
          <>
            <div className="relative flex items-start justify-center gap-3 max-w-5xl mx-auto">
              {/* Timeline Container */}
              <div
                className="flex-1 overflow-hidden relative"
                style={{ height: VISIBLE * CARD_HEIGHT + (VISIBLE - 1) * GAP }}
              >
                <div
                  className="absolute inset-x-0 top-0"
                  style={{
                    transform: `translateY(-${offset}px)`,
                    transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {allExperiences.map((exp, idx) => {
                    const isLeft = idx % 2 === 0;
                    return (
                      <div
                        key={exp.id}
                        className="flex items-center"
                        style={{
                          height: CARD_HEIGHT,
                          marginBottom:
                            idx < allExperiences.length - 1 ? GAP : 0,
                        }}
                      >
                        <div className="flex-1 flex items-center justify-end pr-6">
                          {isLeft ? (
                            <DesktopExperienceCard
                              experience={exp}
                              index={idx}
                            />
                          ) : (
                            <div
                              style={{ height: CARD_HEIGHT }}
                              className="w-full"
                            />
                          )}
                        </div>

                        {/* Timeline Center */}
                        <div className="flex-shrink-0 w-10 flex flex-col items-center relative self-stretch">
                          <div
                            className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[2px] bg-[linear-gradient(to_bottom,transparent,rgba(16,185,129,0.35),rgba(16,185,129,0.35),transparent)]
  shadow-sm"
                          />
                          <div
                            className="relative z-10"
                            style={{ marginTop: CARD_HEIGHT / 2 - 10 }}
                          >
                            <div className="absolute inset-0 w-5 h-5 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 opacity-30 blur-sm" />
                            <div className="relative w-5 h-5 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 shadow-lg ring-2 ring-emerald-500/40 ring-offset-2 ring-offset-white dark:ring-offset-gray-900" />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-2 h-2 rounded-full bg-white shadow-inner" />
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 flex items-center justify-start pl-6">
                          {!isLeft ? (
                            <DesktopExperienceCard
                              experience={exp}
                              index={idx}
                            />
                          ) : (
                            <div
                              style={{ height: CARD_HEIGHT }}
                              className="w-full"
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Navigation Arrows */}
              <div className="flex-shrink-0 flex flex-col gap-3 absolute -right-14 top-1/2 -translate-y-1/2">
                <button
                  onClick={() => scroll("up")}
                  disabled={!canUp || isAnimating}
                  className="w-11 h-11 rounded-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 shadow-lg flex items-center justify-center hover:bg-gradient-to-r hover:from-emerald-500 hover:to-cyan-500 hover:text-white transition-all duration-300 hover:scale-110 disabled:opacity-30"
                >
                  <FaChevronUp className="text-gray-600 dark:text-gray-400 group-hover:text-white text-lg" />
                </button>
                <button
                  onClick={() => scroll("down")}
                  disabled={!canDown || isAnimating}
                  className="w-11 h-11 rounded-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 shadow-lg flex items-center justify-center hover:bg-gradient-to-r hover:from-emerald-500 hover:to-cyan-500 hover:text-white transition-all duration-300 hover:scale-110 disabled:opacity-30"
                >
                  <FaChevronDown className="text-gray-600 dark:text-gray-400 group-hover:text-white text-lg" />
                </button>
              </div>
            </div>

            {/* Progress Indicators */}
            <div className="flex items-center justify-center gap-2 mt-10">
              {allExperiences.map((_, i) => {
                const inView = i >= activeStart && i < activeStart + VISIBLE;
                return (
                  <div
                    key={i}
                    className="rounded-full transition-all duration-300 cursor-pointer hover:scale-110"
                    onClick={() => {
                      if (!isAnimating) {
                        setIsAnimating(true);
                        setOffset(i * STEP);
                        setTimeout(() => setIsAnimating(false), 520);
                      }
                    }}
                    style={{
                      width: inView ? 24 : 6,
                      height: 4,
                      background: inView
                        ? "linear-gradient(to right, #10b981, #06b6d4)"
                        : "#969798ff",
                    }}
                  />
                );
              })}
            </div>

            {/* Counter */}
            <div className="text-center mt-5">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full border border-gray-200 dark:border-gray-700 shadow-md">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {activeStart + 1} –{" "}
                  {Math.min(activeStart + VISIBLE, allExperiences.length)} of{" "}
                  {allExperiences.length} positions
                </span>
              </div>
            </div>
          </>
        )}

        {/* ==================== MOBILE VIEW (Simple Stacked List - ALL Experiences Visible) ==================== */}
        {isMobile && (
          <div className="space-y-4">
            {allExperiences.map((exp) => (
              <MobileExperienceCard key={exp.id} experience={exp} />
            ))}
            <div className="text-center pt-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {allExperiences.length} professional experiences
              </span>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .bg-grid-pattern {
          background-image:
            linear-gradient(to right, #d1d5db 1px, transparent 1px),
            linear-gradient(to bottom, #d1d5db 1px, transparent 1px);
          background-size: 40px 40px;
        }
        .dark .bg-grid-pattern {
          background-image:
            linear-gradient(to right, #374151 1px, transparent 1px),
            linear-gradient(to bottom, #374151 1px, transparent 1px);
        }

        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .gradient-text {
          background: linear-gradient(135deg, #10b981, #06b6d4, #8b5cf6);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
      `}</style>
    </section>
  );
};

export default Experience;
